import i18next from 'i18next'
import React from 'react'
import { getClassName } from '../../../utils/utils'
import { Breadcrumbs } from '../../sharedLayout/breadcrumbs'
import { GenericPage } from '../cmsPageState'
import { Image, ImageBase, ImageOptions } from '../../../utils/images'
import {
  pageheaderLargeSrc,
  pageheaderLargeSrcSet,
  pageheaderSmallSrc,
  pageheaderSmallSrcSet
} from '../../../utils/imageSizes'
import { getImageUrls } from '../../../utils/sharepointImageUrls'
import { Link } from '../../../utils/links'
import { datalayerHandler } from '../../../utils/datalayer'
import { maybeToMailPlusLink } from '../../../utils/mailplus'

type PageProperties = Pick<
  GenericPage,
  | 'path'
  | 'parent'
  | 'title'
  | 'intro'
  | 'image'
  | 'primaryButton'
  | 'secondaryButton'
>

type GenericPageHeaderProps = {
  readSpeakerCookieAccepted: boolean
  extraJSXBelowTitle?: React.ReactNode
  extraJSXBelowIntro?: React.ReactNode
  extraJSXBelowText?: React.ReactNode
  notFoundOrEmptyPage?: boolean
} & PageProperties

export const getBreadcrumb = (
  page: PageProperties
): Array<{
  title: string
  url: string
}> =>
  (page.parent.kind === 'l' ? [] : getBreadcrumb(page.parent.v)).concat({
    url: page.path,
    title: page.title
  })

export const GenericPageHeader = (props: GenericPageHeaderProps) => {
  const {
    title,
    intro,
    image,
    primaryButton,
    secondaryButton,
    notFoundOrEmptyPage
  } = props

  const fullsize = false
  const inverted = false

  return (
    <header
      className={`pageheader pageheader--${
        image ? 'image' : 'text'
      } pageheader--${fullsize ? 'fw' : 'cw'}`}
    >
      <div className="pageheader__inner">
        <div className="pageheader__content container--s gutter--m spacing--m">
          <h1 className="pageheader__title title--l">{title}</h1>

          {intro && <p className="pageheader__intro">{intro}</p>}

          {(primaryButton?.slug || secondaryButton?.slug) && (
            <div className="pageheader__clickables">
              {primaryButton?.slug && (
                <div className="pageheader__clickable">
                  <Link
                    url={primaryButton.slug}
                    className={`button button--primary${
                      inverted ? '-inverted' : ''
                    }`}
                    onClick={() =>
                      datalayerHandler('header_cta', primaryButton.title, 'header_cta', {
                        link: maybeToMailPlusLink(primaryButton.slug)
                      })}
                  >
                    <span className="button__label">{primaryButton.title}</span>
                  </Link>
                </div>
              )}

              {secondaryButton?.slug && (
                <div className="pageheader__clickable">
                  <Link
                    url={secondaryButton.slug}
                    className={`button button--secondary${
                      inverted ? '-inverted' : ''
                    }`}
                    onClick={() =>
                      datalayerHandler('header_cta', secondaryButton.title, 'header_cta', {
                        link: maybeToMailPlusLink(secondaryButton.slug)
                      })}
                  >
                    <span className="button__label">
                      {secondaryButton.title}
                    </span>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="pageheader__visual" aria-hidden="true">
          <div
            className={`${
              fullsize ? 'pageheader__figure' : 'container--m gutter--m'
            }`}
          >
            <div className="pageheader__media">
              {image && !notFoundOrEmptyPage && (
                <Image
                  src={fullsize ? pageheaderLargeSrc : pageheaderSmallSrc}
                  srcset={
                    fullsize ? pageheaderLargeSrcSet : pageheaderSmallSrcSet
                  }
                  lazyload={false}
                  sizes={fullsize ? "100vw" : "(min-width: 1040px) 1040px, 100vw"}
                  urls={getImageUrls(image)}
                  classNames={{
                    image: 'pageheader__image'
                  }}
                  width={fullsize ? '1560' : '1040'}
                  height={fullsize ? '600' : '434'}
                  keepOriginalSize={false}
                />
              )}
              {notFoundOrEmptyPage && !!image && (
                <img
                  className="pageheader__image"
                  src={image}
                  alt=""
                  width={fullsize ? '1560' : '1040'}
                  height={fullsize ? '600' : '434'}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
